<template>
  <div class="production-container">
    <div v-if="!loaded" class="loading">
      <i class="icon icon-spinner"></i>
    </div>

    <div v-if="showErrorState" class="centered-content">
      <div class="error-state">
        <i class="fas fa-exclamation-triangle fa-4x"></i>

        <div class="error-state-body">
          <h1>Uh oh</h1>

          <p>We ran into an issue while attempting to load this Production.</p>

          <button
            class="tb-btn tb-btn-contained medium-em"
            @click="handleOnClickContactSupport($event)"
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>

    <div v-if="!showErrorState && loaded" class="flex-row flex-grow view-production">
      <div id="leftRail" class="flex-column left-rail">
        <resource-library
          :pinned="assetLibraryPinned"
          @onPinAssetLibrary="handlePinAssetLibrary"
        />
      </div>

      <div
        id="productionContent"
        ref="productionContent"
        class="grid-container flex-grow production-content"
      >
        <div class="flex-column flex-grow">
          <live-preview output="preview" />
          <timeline />
        </div>

        <div class="flex-column flex-grow">
          <live-preview />
          <layer-editor />
        </div>
      </div>

      <post-modal />

      <update-production-modal
        v-if="showUpdateProductionModal"
        @close="handleCloseUpdateProductionModal"
      />

      <screen-modal
        v-if="showManageScreensModal"
        @close="handleCloseManageScreensModal"
      />
    </div>
  </div>
</template>

<script>
import LivePreview from '../../LivePreview.vue';
import LayerEditorComponent from '../../layer-editor/LayerEditor.vue';
import TimelineComponent from '../../timeline/Timeline.vue';
import ScreenModalComponent from '../../connect-screen/ScreenModal.vue';
import ResourceLibraryComponent from '../resource-library/ResourceLibrary.vue';
import UpdateProductionComponent from '../../update-production/UpdateProduction.vue';
import PostModalComponent from '../../modals/Modal.vue';

export default {
  name: 'ProductionComponent',

  components: {
    LivePreview,
    'post-modal': PostModalComponent,
    'screen-modal': ScreenModalComponent,
    'update-production-modal': UpdateProductionComponent,
    timeline: TimelineComponent,
    'layer-editor': LayerEditorComponent,
    'resource-library': ResourceLibraryComponent,
  },

  data() {
    return {
      assetLibraryPinned: JSON.parse(localStorage.getItem('assetLibraryPinned') || 'true') === true,
      collapsed: false,
      showErrorState: false,
      loaded: false,
      editing: false,
    };
  },

  created() {
    this.load();
  },

  methods: {
    /**
    * Load EVERYTHING.
    * This is pretty annoying, and needs to be broken up into junks that can be retried individually.
    * Otherwise, if ANYTHING fails to load, the user will see a broken production screen.
    */
    async load() {
      try {
        // Do initial load of production
        await this.$store.dispatch('getProductionV2', this.$route.params.production_id);

        // Update page title to reflect production
        if (this.production?.title) {
          document.querySelector('title').innerText = `${this.production?.title} - Tagboard Producer`;
        }

        // Join production socket room to listen for updates
        this.$socket.emit('production', { production_id: this.$route.params.production_id });

        await Promise.all([
          // Get permissions, so we know if we should show the smart panels tab
          this.$store.dispatch('getPermissions'),

          // Saved Graphics (not normal graphic templates)
          this.$store.dispatch('getGraphics'),

          // Load assets currently in the timeline
          // so they are added to their respective lookup tables
          this.$store.dispatch('getTimelineAssets'),
        ]);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('Error loading production:', err);
        this.showErrorState = true;
      }

      this.loaded = true;

      // Backfill any missing short URLs for QR codes in the timeline
      this.$store.dispatch('generateShortUrls');
      this.$store.dispatch('trackSportradarInGameElement');
    },

    cancel() {
      this.editing = false;
      this.title = this.production.title;
    },

    done() {
      this.editing = false;
    },

    handleCloseUpdateProductionModal() {
      this.$store.dispatch('toggleUpdateProductionModal');
    },

    handleCloseManageScreensModal() {
      this.$store.dispatch('toggleScreensModal');
    },

    handleCloseHighlightsModal() {
      this.$store.dispatch('toggleHighlightsModal');
    },

    handlePinAssetLibrary(pinned) {
      this.assetLibraryPinned = pinned;
      localStorage.setItem('assetLibraryPinned', pinned);
    },

    handleOnClickContactSupport() {
      this.$store.dispatch('showNewIntercomMessage');
    },
  },

  computed: {
    has_panels() {
      return this.$store.state.user.has_panels;
    },

    production() {
      return this.$store.state.production.production;
    },

    showUpdateProductionModal() {
      return this.$store.state.clients.showUpdateProductionModal;
    },

    showManageScreensModal() {
      return this.$store.state.clients.showScreensModal;
    },

    showWHighlightsModal() {
      return this.$store.state.clients.showHighlightsModal;
    },
  },
};
</script>
